import { useEffect, useState } from "react";
import { Loader2, Save, KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { getPrefs, updatePrefs, listCategories, changePassword, formatApiError } from "@/lib/inmindApi";
import { useAuth } from "@/lib/AuthContext";
import { categoryLabel } from "@/components/CategoryBadge";

const COMMON_TZ = [
  "Asia/Kolkata", "Asia/Singapore", "Asia/Tokyo", "Asia/Dubai",
  "Europe/London", "Europe/Paris", "Europe/Berlin",
  "America/New_York", "America/Chicago", "America/Los_Angeles",
  "Australia/Sydney", "UTC",
];

export default function PreferencesPage() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([getPrefs(), listCategories()])
      .then(([p, c]) => { setPrefs(p); setCats(c); })
      .catch(() => toast.error("Couldn't load preferences"));
  }, []);

  const toggleCat = (cat) => {
    const cur = new Set(prefs.preferred_categories || []);
    if (cur.has(cat)) cur.delete(cat); else cur.add(cat);
    setPrefs({ ...prefs, preferred_categories: Array.from(cur) });
  };

  const save = async () => {
    const cats = prefs.preferred_categories || [];
    if (cats.length === 0) {
      toast.error("Pick at least one subject");
      return;
    }
    setSaving(true);
    try {
      const updated = await updatePrefs(prefs);
      setPrefs(updated);
      toast.success("Preferences saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) {
    return <div className="flex items-center justify-center py-24 text-[var(--inmind-muted)]"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>;
  }

  const active = new Set(prefs.preferred_categories || []);

  return (
    <div className="space-y-5" data-testid="preferences-page">
      <div>
        <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--inmind-muted)]">Account</div>
        <h2 className="font-serif text-2xl sm:text-3xl mt-1 text-[var(--inmind-text)] tracking-tight">Your preferences</h2>
        <p className="text-sm text-[var(--inmind-text-secondary)] mt-2">
          Signed in as <span className="font-medium text-[var(--inmind-text)]">{user?.email}</span>
          {user?.auth_providers?.length > 0 && (
            <span className="ml-2 text-xs uppercase tracking-wider text-[var(--inmind-muted)]">
              via {user.auth_providers.join(" + ")}
            </span>
          )}
        </p>
      </div>

      <Card className="rounded-2xl border border-black/5 bg-white shadow-[0_4px_18px_rgba(0,0,0,0.03)]">
        <CardContent className="p-6 space-y-6">
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--inmind-muted)] mb-1">
              Subjects you want to learn
            </div>
            <p className="text-sm text-[var(--inmind-text-secondary)] mb-3">
              Today's card is drawn from the subjects you pick.
            </p>
            <div className="flex flex-wrap gap-2" data-testid="pref-cats">
              {cats.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleCat(c)}
                  data-testid={`pref-cat-${c}`}
                  className={`text-sm rounded-full px-3.5 py-1.5 transition-colors ${
                    active.has(c)
                      ? "bg-[var(--inmind-primary)] text-white"
                      : "bg-white text-[var(--inmind-text-secondary)] border border-black/10 hover:bg-black/5"
                  }`}
                >
                  {categoryLabel(c)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--inmind-muted)] mb-1">
              Daily learning time
            </div>
            <p className="text-sm text-[var(--inmind-text-secondary)] mb-2">
              The time of day you usually pull your card. We use it to time today's date for you.
            </p>
            <Input
              type="time"
              value={prefs.notify_time || "08:00"}
              onChange={(e) => setPrefs({ ...prefs, notify_time: e.target.value })}
              data-testid="pref-time"
              className="h-11 rounded-xl max-w-[160px]"
            />
          </div>

          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--inmind-muted)] mb-1">
              Timezone
            </div>
            <select
              value={prefs.timezone || "UTC"}
              onChange={(e) => setPrefs({ ...prefs, timezone: e.target.value })}
              data-testid="pref-timezone"
              className="h-11 rounded-xl border border-black/10 bg-white px-3 text-sm w-full max-w-xs"
            >
              {COMMON_TZ.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>

          <Button
            onClick={save}
            disabled={saving}
            data-testid="pref-save"
            className="rounded-xl px-5 py-3 bg-[var(--inmind-primary)] text-white hover:bg-[var(--inmind-primary-hover)] transition-colors gap-1.5"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={1.8} />}
            Save preferences
          </Button>
        </CardContent>
      </Card>

      <PasswordCard user={user} />
    </div>
  );
}

function PasswordCard({ user }) {
  const hasPassword = (user?.auth_providers || []).includes("password");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (next.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (hasPassword && !current) {
      toast.error("Please enter your current password");
      return;
    }
    setBusy(true);
    try {
      await changePassword({
        current_password: hasPassword ? current : undefined,
        new_password: next,
      });
      toast.success(hasPassword ? "Password changed" : "Password set");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="rounded-2xl border border-black/5 bg-white shadow-[0_4px_18px_rgba(0,0,0,0.03)]" data-testid="password-card">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-[var(--inmind-primary)]" strokeWidth={1.8} />
          <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--inmind-muted)]">
            {hasPassword ? "Change password" : "Set a password"}
          </div>
        </div>
        <p className="text-sm text-[var(--inmind-text-secondary)]">
          {hasPassword
            ? "Forgot your old one? Just set a new password here — it replaces the previous one immediately."
            : "You signed up with Google. Set a password so you can also sign in with email + password from any device."}
        </p>

        {hasPassword && (
          <PasswordField
            label="Current password"
            value={current}
            onChange={setCurrent}
            visible={showCurrent}
            onToggle={() => setShowCurrent((v) => !v)}
            testId="pw-current"
          />
        )}
        <PasswordField
          label="New password (6+ characters)"
          value={next}
          onChange={setNext}
          visible={showNew}
          onToggle={() => setShowNew((v) => !v)}
          testId="pw-new"
        />
        <PasswordField
          label="Confirm new password"
          value={confirm}
          onChange={setConfirm}
          visible={showNew}
          onToggle={() => setShowNew((v) => !v)}
          testId="pw-confirm"
        />

        <Button
          onClick={submit}
          disabled={busy}
          data-testid="pw-submit"
          className="rounded-xl px-5 py-3 bg-[var(--inmind-primary)] text-white hover:bg-[var(--inmind-primary-hover)] transition-colors gap-1.5"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" strokeWidth={1.8} />}
          {hasPassword ? "Update password" : "Set password"}
        </Button>
      </CardContent>
    </Card>
  );
}

function PasswordField({ label, value, onChange, visible, onToggle, testId }) {
  return (
    <div>
      <label className="block text-xs font-medium text-[var(--inmind-text-secondary)] mb-1">{label}</label>
      <div className="relative">
        <Input
          type={visible ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className="h-11 rounded-xl pr-10"
          autoComplete="new-password"
        />
        <button
          type="button"
          onClick={onToggle}
          aria-label={visible ? "Hide password" : "Show password"}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--inmind-muted)] hover:text-[var(--inmind-text)]"
        >
          {visible ? <EyeOff className="w-4 h-4" strokeWidth={1.8} /> : <Eye className="w-4 h-4" strokeWidth={1.8} />}
        </button>
      </div>
    </div>
  );
}
